import gqlmin from 'gqlmin'
import { datoPageContentQuery } from '@/src/queries/query-page'
import { datoRateLimitedRequest } from '@/src/utils/contentful'

// Global caches outside the function scope, accessible across all concurrent GSP runs
const contentCache = {}
const inFlightRequests = {}

/**
 * Core fetching logic with request deduplication (caching in memory).
 * This prevents multiple concurrent GSP calls from hitting the API with the same query.
 */
export async function fetchPageContent(slug, locale, marketId) {
  const cacheKey = `${slug}-${locale}-${marketId}`

  // 1. CHECK CACHE: Return content if already successfully fetched
  if (contentCache[cacheKey]) {
    return contentCache[cacheKey]
  }

  // 2. CHECK IN-FLIGHT: If a request is running, wait for it
  if (inFlightRequests[cacheKey]) {
    console.log(`[Dedupe Hit] Waiting for content for: ${cacheKey}`)
    return inFlightRequests[cacheKey]
  }

  // 3. CACHE MISS & NO IN-FLIGHT: Initiate a new fetch with retry logic
  const fetchPromise = (async () => {
    try {
      let result = await datoRateLimitedRequest(gqlmin(datoPageContentQuery()), {
        slug: slug,
        marketId: [marketId],
        locale: locale,
        componentsLocale: locale
      })

      // Handle the locale fallback logic you already have
      if (result.pageModel === null) {
        result = await datoRateLimitedRequest(gqlmin(datoPageContentQuery()), {
          slug: slug,
          marketId: [marketId],
          locale: 'en',
          componentsLocale: locale
        })
      }

      const pageModel = result.pageModel || null

      // Save result to cache before returning
      if (pageModel) {
        contentCache[cacheKey] = pageModel
      }

      // Clean up the in-flight tracker regardless of success/failure
      delete inFlightRequests[cacheKey]

      return pageModel
    } catch (error) {
      // If the request fails (even after retries), clear the tracker and re-throw
      delete inFlightRequests[cacheKey]
      console.error(`Fatal error in fetchPageContent for ${cacheKey}`, error)
      throw error // Let the outer getContentBySlug handle the failure
    }
  })()

  // Track this promise so subsequent concurrent calls wait for it
  inFlightRequests[cacheKey] = fetchPromise

  return fetchPromise
}

// Error handling and retry mechanism (Simplified since backoff is in datoRateLimitedRequest)
export async function handleFetchError(error, retries, slug, locale, marketId) {
  // NOTE: Your original logic here only retries *once* after the main fetch fails.
  // The exponential backoff inside datoRateLimitedRequest is more robust.
  // We'll trust the inner function to handle retries and just log/return on final failure.

  if (error?.response?.status === 429 && retries > 0) {
    console.warn(
      `Final 429 hit (outer retry). Returning null to prevent build failure. ${slug} - ${locale} - ${marketId}`
    )
    return null
  }

  console.error('Error fetching content by slug:', error, slug, locale, marketId)
  return null
}

export async function getContentBySlug(slug, locale, marketId, retries = 0) {
  if (!slug || !marketId) return null

  try {
    // Call the function with deduplication and caching
    const pageContent = await fetchPageContent(slug, locale, marketId)
    return pageContent
  } catch (error) {
    // If a fatal error occurs (e.g., all 5 inner retries failed)
    return handleFetchError(error, retries, slug, locale, marketId)
  }
}
